import React from 'react';

import { Box, Col, getCSSVariable, Row, Text } from '@opendoor/bricks-next';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { ICompareFees } from 'declarations/contentful';

const RenderCompareFees = (entry: ICompareFees) => {
  const { fields } = entry;

  return (
    <Box backgroundColor="$backgroundPrimary" py="$40x" $smallerThanMD={{ py: '$24x' }}>
      <Box display="block" maxWidth={1120} px="$12x" width="100%" mx="auto">
        <Col gap="$12x">
          <Col gap="$6x">
            <Text tag="p" typography="$labelSmall" fontWeight="$medium" color="$contentAccentBrand">
              {fields.eyebrow}
            </Text>
            <Text
              tag="h2"
              typography="$subheaderLarge"
              color="$contentPrimary"
              $smallerThanMD={{ typography: '$subheaderMedium' }}
            >
              {fields.title}
            </Text>
            <Text
              tag="p"
              typography="$bodyLarge"
              color="$contentSecondary"
              maxWidth={580}
              $platform-web={{ lineHeight: '1.6' }}
            >
              {fields.description}
            </Text>
          </Col>

          <Box flexDirection="column" gap="$16x" $largerThanSM={{ flexDirection: 'row' }}>
            {/* Competitor fee stack */}
            <Col
              flex={1}
              backgroundColor="$backgroundSecondary"
              borderRadius="$8x"
              p="$20x"
              $smallerThanMD={{ p: '$12x' }}
              gap="$16x"
            >
              <Text tag="h3" typography="$subheaderSmall" color="$contentPrimary">
                {fields.competitorCardTitle}
              </Text>
              <Col gap="$4x">
                {fields.competitorFees.map((fee, idx) => (
                  <React.Fragment key={fee.sys.id}>
                    <Col
                      p="$10x"
                      borderRadius="$6x"
                      gap="$1x"
                      backgroundColor="$backgroundStateErrorSecondary"
                      borderWidth={1}
                      borderColor="$borderStateInactiveAccentError"
                    >
                      <Text tag="span" typography="$labelSmall" color="$contentSecondary">
                        {fee.fields.label}
                      </Text>
                      <Row gap="$4x" alignItems="baseline">
                        <Text
                          tag="span"
                          typography="$subheaderXsmall"
                          fontWeight="$medium"
                          color="$contentStateErrorDefault"
                        >
                          {fee.fields.value}
                        </Text>
                        {fee.fields.subValue && (
                          <Text
                            tag="span"
                            typography="$bodySmall"
                            fontWeight="$medium"
                            color="$contentSecondary"
                          >
                            {fee.fields.subValue}
                          </Text>
                        )}
                      </Row>
                      <Text tag="span" typography="$bodyXsmall" color="$contentSecondary">
                        {fee.fields.note}
                      </Text>
                    </Col>
                    {idx < fields.competitorFees.length - 1 && (
                      <Text
                        tag="span"
                        typography="$bodyXlarge"
                        fontWeight="$medium"
                        color="$contentStateErrorDefault"
                        textAlign="center"
                        py="$1x"
                      >
                        +
                      </Text>
                    )}
                  </React.Fragment>
                ))}
              </Col>
              <Col
                p="$10x"
                borderRadius="$6x"
                gap="$1x"
                borderLeftWidth={3}
                borderLeftColor="$contentStateErrorDefault"
                backgroundColor="$backgroundStateErrorSecondary"
              >
                <Text tag="span" typography="$labelSmall" color="$contentSecondary">
                  {fields.competitorTotalLabel}
                </Text>
                <Text
                  tag="span"
                  typography="$bodyXlarge"
                  fontWeight="$medium"
                  color="$contentStateErrorDefault"
                >
                  {fields.competitorTotalValue}
                </Text>
                {fields.competitorAttribution && (
                  <Text tag="span" typography="$bodyXsmall" color="$contentTertiary">
                    {fields.competitorAttribution}
                  </Text>
                )}
              </Col>
            </Col>

            {/* Opendoor simplicity */}
            <Col
              flex={1}
              backgroundColor="$backgroundPrimary"
              borderRadius="$8x"
              p="$20x"
              $smallerThanMD={{ p: '$12x' }}
              gap="$16x"
              borderWidth={2}
              borderColor="$borderAccentBrand"
            >
              <Text tag="h3" typography="$subheaderSmall" color="$contentPrimary">
                {fields.opendoorCardTitle}
              </Text>
              <Col gap="$4x">
                <Col
                  p="$10x"
                  borderRadius="$6x"
                  gap="$1x"
                  backgroundColor="$backgroundSecondary"
                  borderWidth={1}
                  borderColor="$borderSecondary"
                >
                  <Text tag="span" typography="$labelSmall" color="$contentSecondary">
                    {fields.opendoorFeeLabel}
                  </Text>
                  <Text
                    tag="span"
                    typography="$subheaderXsmall"
                    fontWeight="$medium"
                    color="$contentStateSuccessDefault"
                  >
                    {fields.opendoorFeeValue}
                  </Text>
                  <Text tag="span" typography="$bodyXsmall" color="$contentSecondary">
                    {fields.opendoorFeeNote}
                  </Text>
                </Col>
              </Col>
              <Col gap="$8x">
                {fields.opendoorBenefits.map((benefit, idx) => (
                  <Row key={idx} gap="$6x" alignItems="center">
                    <IconCircleCheckFilled
                      size={20}
                      color={getCSSVariable('$contentStateSuccessDefault', 'color')}
                    />
                    <Text
                      tag="span"
                      typography="$bodyMedium"
                      fontWeight="$medium"
                      color="$contentPrimary"
                    >
                      {benefit}
                    </Text>
                  </Row>
                ))}
              </Col>
              <Col
                p="$10x"
                borderRadius="$6x"
                gap="$1x"
                borderLeftWidth={3}
                borderLeftColor="$borderAccentBrand"
                backgroundColor="$backgroundSecondary"
              >
                <Text tag="span" typography="$labelSmall" color="$contentSecondary">
                  {fields.opendoorBottomLineLabel}
                </Text>
                <Text
                  tag="span"
                  typography="$bodyXlarge"
                  fontWeight="$medium"
                  color="$contentAccentBrand"
                >
                  {fields.opendoorBottomLineValue}
                </Text>
              </Col>
            </Col>
          </Box>
        </Col>
      </Box>
    </Box>
  );
};

const CompareFees: EntryComponent<ICompareFees> = {
  render: RenderCompareFees,
};

export default CompareFees;
